// ============================================
// Settings Hooks - TanStack Query
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { SystemSetting } from '@conai/shared-types';
import { useSettingsService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';

type GeneralSettings = {
  store_name: string;
  store_email: string;
  timezone: string;
  currency: string;
  language: string;
};

type NotificationSettings = {
  email_notifications: boolean;
  low_stock_alerts: boolean;
  order_notifications: boolean;
  daily_reports: boolean;
  weekly_digest: boolean;
};

type SecuritySettings = {
  two_factor_enabled: boolean;
  session_timeout: number;
  password_expiry: number;
  ip_whitelist: string;
};

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to get all settings
 */
export function useAllSettings() {
  const settingsService = useSettingsService();

  return useQuery({
    queryKey: queryKeys.settings.lists(),
    queryFn: () => settingsService.getAll(),
  });
}

/**
 * Tham số vận hành dạng khoá–giá trị (sheet 2 mục #234) — GET /settings/.
 *
 * KHÁC HẲN bốn tab cấu hình cố định (general/notifications/security/
 * integrations): đó là các form có sẵn field, còn đây là bảng khoá tuỳ ý mà BE
 * định nghĩa, FE không biết trước có những khoá gì.
 *
 * `is_public` chia hai nhóm: khoá công khai lộ ra ngoài (app khách đọc được),
 * khoá nội bộ thì không. Phân biệt được là quan trọng — sửa nhầm một khoá
 * công khai là đổi hành vi của app khách ngay lập tức.
 */
export function useSystemSettings(params?: { is_public?: boolean; search?: string }) {
  const settingsService = useSettingsService();

  return useQuery({
    queryKey: [...queryKeys.settings.all, 'system', params ?? {}] as const,
    queryFn: () => settingsService.list(params),
  });
}

/**
 * Sửa MỘT khoá — PATCH /settings/{key}/.
 *
 * Cố ý KHÔNG dùng `bulkUpdate`: lưu từng khoá một thì lỗi ở khoá này không kéo
 * theo khoá khác, và nhật ký audit ghi ra đúng một dòng cho mỗi thay đổi —
 * đúng thứ màn lịch sử bên dưới cần để dựng diff từng lần sửa.
 */
export function useUpdateSystemSetting() {
  const settingsService = useSettingsService();
  const queryClient = useQueryClient();

  return useMutation({
    // Kiểu đúng của `value`, KHÔNG ép `as never`: ép thì mọi thứ lọt qua, kể
    // cả `undefined` — mà `undefined` gửi lên là xoá trắng một tham số hệ thống.
    mutationFn: ({ key, value }: { key: string; value: SystemSetting['value'] }) =>
      settingsService.update(key, { value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.all });
      // Lịch sử thay đổi nằm ở /audit/ — dọn luôn, nếu không thì bảng lịch sử
      // ngay bên cạnh vẫn hiện dữ liệu trước khi sửa.
      queryClient.invalidateQueries({ queryKey: queryKeys.auditLogs.all });
      toast.success('Setting saved');
    },
    onError: (error: unknown) => {
      toast.error((error as { message?: string })?.message ?? 'Could not save setting');
    },
  });
}

/**
 * Hook to get general settings
 */
export function useGeneralSettings() {
  const settingsService = useSettingsService();

  return useQuery({
    queryKey: queryKeys.settings.general(),
    queryFn: () => settingsService.getGeneral(),
  });
}

/**
 * Hook to get notification settings
 */
export function useNotificationSettings() {
  const settingsService = useSettingsService();

  return useQuery({
    queryKey: queryKeys.settings.notifications(),
    queryFn: () => settingsService.getNotifications(),
  });
}

/**
 * Hook to get security settings
 */
export function useSecuritySettings() {
  const settingsService = useSettingsService();

  return useQuery({
    queryKey: queryKeys.settings.security(),
    queryFn: () => settingsService.getSecurity(),
  });
}

/**
 * Hook to get integrations
 */
export function useIntegrations() {
  const settingsService = useSettingsService();

  return useQuery({
    queryKey: queryKeys.settings.integrations(),
    queryFn: () => settingsService.getIntegrations(),
  });
}

/**
 * Hook to get settings stats
 */
export function useSettingsStats() {
  const settingsService = useSettingsService();

  return useQuery({
    queryKey: queryKeys.settings.stats(),
    queryFn: () => settingsService.getStats(),
  });
}

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to update general settings
 */
export function useUpdateGeneralSettings() {
  const settingsService = useSettingsService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<GeneralSettings>) => settingsService.updateGeneral(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.general() });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.lists() });
      toast.success('General settings updated');
    },
  });
}

/**
 * Hook to update notification settings
 */
export function useUpdateNotificationSettings() {
  const settingsService = useSettingsService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<NotificationSettings>) => settingsService.updateNotifications(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.notifications() });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.lists() });
      toast.success('Notification settings updated');
    },
  });
}

/**
 * Hook to update security settings
 */
export function useUpdateSecuritySettings() {
  const settingsService = useSettingsService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<SecuritySettings>) => settingsService.updateSecurity(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.security() });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.lists() });
      toast.success('Security settings updated');
    },
  });
}

/**
 * Hook to update integration config
 */
export function useUpdateIntegration() {
  const settingsService = useSettingsService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, config }: { name: string; config: Record<string, unknown> }) =>
      settingsService.updateIntegration(name, config),
    onSuccess: (integration: { name: string }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.integrations() });
      toast.success(`Integration "${integration.name}" updated`);
    },
  });
}

/**
 * Hook to connect an integration
 */
export function useConnectIntegration() {
  const settingsService = useSettingsService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => settingsService.connectIntegration(name),
    onSuccess: (integration: { name: string }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.integrations() });
      toast.success(`Integration "${integration.name}" connected`);
    },
  });
}

/**
 * Hook to disconnect an integration
 */
export function useDisconnectIntegration() {
  const settingsService = useSettingsService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => settingsService.disconnectIntegration(name),
    onSuccess: (integration: { name: string }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.integrations() });
      toast.success(`Integration "${integration.name}" disconnected`);
    },
  });
}

/**
 * Hook to reset settings to defaults
 */
export function useResetSettings() {
  const settingsService = useSettingsService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => settingsService.resetToDefaults(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.all });
      toast.success('Settings reset to defaults');
    },
  });
}

// ============================================
// Combined Settings Hook
// ============================================

/**
 * Hook to get all settings with loading state
 */
export function useSettings() {
  const { data: general, isLoading: generalLoading, error: generalError } = useGeneralSettings();
  const { data: notifications, isLoading: notificationsLoading, error: notificationsError } = useNotificationSettings();
  const { data: security, isLoading: securityLoading, error: securityError } = useSecuritySettings();
  const { data: integrations, isLoading: integrationsLoading, error: integrationsError } = useIntegrations();

  const isLoading = generalLoading || notificationsLoading || securityLoading || integrationsLoading;
  const error = generalError || notificationsError || securityError || integrationsError;

  return {
    general,
    notifications,
    security,
    integrations,
    isLoading,
    error,
  };
}
